import React, {useMemo, useState} from 'react';
import {router} from '@inertiajs/react';
import {
    Accordion,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    InputGroup,
    ListGroup,
    Row,
} from 'react-bootstrap';

const areaLabels = {
    activitylogs: 'Aktivitätsprotokoll',
    campaigns: 'Kampagnen',
    cronjob: 'System',
    dashboard: 'Dashboard',
    departments: 'Abteilungen',
    export: 'Export',
    import: 'Import',
    mail: 'E-Mail',
    orders: 'Bestellungen',
    products: 'Produkte',
    roles: 'Rollen & Rechte',
    settings: 'Einstellungen',
    stock: 'Lager',
    users: 'Benutzerverwaltung',
    weclapp: 'Weclapp',
};

const areaLabel = permissionName => (
    areaLabels[permissionName.split('.')[0]] || 'Weitere Bereiche'
);
const roleLabel = name => (name === 'superadmin' ? 'Superadmin' : name);

export default function Roles({
    roles = [],
    permissions = [],
    can_manage_roles: canManage = false,
}) {
    const [newRoleName, setNewRoleName] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState(roles[0]?.id);
    const selectedRole = roles.find(role => role.id === selectedRoleId) || roles[0];

    const permissionGroups = useMemo(() => permissions.reduce((groups, permission) => {
        const group = areaLabel(permission.name);
        (groups[group] ??= []).push(permission);
        return groups;
    }, {}), [permissions]);

    const createRole = event => {
        event.preventDefault();
        router.post('/roles', {name: newRoleName}, {
            onSuccess: () => setNewRoleName(''),
        });
    };

    const togglePermission = permission => {
        const currentIds = selectedRole.permissions.map(item => item.id);
        const permissionsToSave = currentIds.includes(permission.id)
            ? currentIds.filter(id => id !== permission.id)
            : [...currentIds, permission.id];

        router.post('/roles/' + selectedRole.id + '/permissions', {
            permissions: permissionsToSave,
        });
    };

    const deleteSelectedRole = () => {
        if (window.confirm('Rolle „' + selectedRole.name + '“ wirklich löschen?')) {
            router.delete('/roles/' + selectedRole.id);
        }
    };

    if (! selectedRole) {
        return (
            <Container fluid className="py-3">
                <h1>Rollenverwaltung</h1>
                <p className="text-muted">Noch keine Rollen vorhanden.</p>
            </Container>
        );
    }

    const selectedIsLocked = selectedRole.name === 'superadmin';

    return (
        <Container fluid className="py-3">
            <div className="mb-3">
                <h1 className="mb-0">Rollenverwaltung</h1>
                <small className="text-muted">
                    Rolle auswählen und Rechte übersichtlich nach Funktionsbereich vergeben.
                </small>
            </div>

            {canManage && (
                <Form onSubmit={createRole} className="mb-4">
                    <InputGroup style={{maxWidth: 420}}>
                        <Form.Control
                            required
                            value={newRoleName}
                            onChange={event => setNewRoleName(event.target.value)}
                            placeholder="Neue Rolle"
                        />
                        <Button type="submit">Rolle anlegen</Button>
                    </InputGroup>
                </Form>
            )}

            <Row className="g-3 align-items-start">
                <Col lg={4} xl={3}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white fw-semibold">Rollen</Card.Header>
                        <ListGroup variant="flush">
                            {roles.map(role => {
                                const locked = role.name === 'superadmin';
                                return (
                                    <ListGroup.Item
                                        key={role.id}
                                        action
                                        active={role.id === selectedRole.id}
                                        onClick={() => setSelectedRoleId(role.id)}
                                        className="d-flex justify-content-between align-items-center"
                                    >
                                        <span>
                                            {roleLabel(role.name)}
                                            {locked && <span aria-label="geschützt" className="ms-1">🔒</span>}
                                        </span>
                                        <Badge
                                            bg={role.id === selectedRole.id ? 'light' : 'secondary'}
                                            text={role.id === selectedRole.id ? 'dark' : undefined}
                                            pill
                                        >
                                            {locked ? 'alle' : role.permissions.length}
                                        </Badge>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    </Card>
                </Col>

                <Col lg={8} xl={9}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white d-flex flex-wrap gap-2 justify-content-between align-items-center">
                            <div className="fw-semibold">
                                {roleLabel(selectedRole.name)}
                                {selectedIsLocked && (
                                    <Badge bg="dark" pill className="ms-2">
                                        Vollzugriff · gesperrt
                                    </Badge>
                                )}
                            </div>
                            {! selectedIsLocked && (
                                <Badge bg="light" text="dark">
                                    {selectedRole.permissions.length} Rechte
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {selectedIsLocked ? (
                                <p className="mb-0 text-muted">
                                    Superadmin erhält automatisch alle bestehenden und neuen
                                    Berechtigungen. Diese Rolle kann nicht verändert oder gelöscht
                                    werden.
                                </p>
                            ) : (
                                <>
                                    <Accordion alwaysOpen>
                                        {Object.entries(permissionGroups).map(([group, items], index) => {
                                            const enabledCount = items.filter(permission => (
                                                selectedRole.permissions.some(item => item.id === permission.id)
                                            )).length;

                                            return (
                                                <Accordion.Item eventKey={String(index)} key={group}>
                                                    <Accordion.Header>
                                                        {group}
                                                        <Badge bg="secondary" pill className="ms-2">
                                                            {enabledCount}/{items.length}
                                                        </Badge>
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        {items.map(permission => (
                                                            <Form.Check
                                                                key={permission.id}
                                                                className="mb-2"
                                                                type="switch"
                                                                disabled={! canManage}
                                                                checked={selectedRole.permissions.some(
                                                                    item => item.id === permission.id,
                                                                )}
                                                                onChange={() => togglePermission(permission)}
                                                                label={(
                                                                    <>
                                                                        <span>{permission.title || permission.name}</span>
                                                                        <small className="text-muted ms-2">
                                                                            {permission.name}
                                                                        </small>
                                                                    </>
                                                                )}
                                                            />
                                                        ))}
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>

                                    {canManage && (
                                        <Button
                                            className="mt-3"
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={deleteSelectedRole}
                                        >
                                            Rolle löschen
                                        </Button>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
